export interface RepeatingSeasonRange {
  start_date: string;
  end_date: string;
  repeat_type?: "none" | "yearly" | "weekly";
  /** JavaScript weekday: Sunday=0, Saturday=6. */
  weekly_days?: number[] | null;
}

function leap(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function parts(value: string): [number, number, number] | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const days = [
    31,
    leap(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= days[month - 1]
    ? [year, month, day]
    : null;
}

/** Leap-day anniversaries use February 28 in non-leap years, matching PostgreSQL. */
function shiftYear(value: string, delta: number): string {
  const [originalYear, month, originalDay] = parts(value)!;
  const year = originalYear + delta;
  const day =
    month === 2 && originalDay === 29 && !leap(year) ? 28 : originalDay;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function validSeasonRange(season: RepeatingSeasonRange): boolean {
  if (
    !parts(season.start_date) ||
    !parts(season.end_date) ||
    season.end_date < season.start_date
  )
    return false;
  if (
    season.repeat_type &&
    season.repeat_type !== "none" &&
    season.repeat_type !== "yearly" &&
    season.repeat_type !== "weekly"
  )
    return false;
  if (season.repeat_type === "weekly") {
    const days = season.weekly_days;
    return Array.isArray(days) && days.length > 0 && days.length <= 7 &&
      new Set(days).size === days.length &&
      days.every((day) => Number.isInteger(day) && day >= 0 && day <= 6);
  }
  return (
    season.repeat_type !== "yearly" ||
    season.end_date < shiftYear(season.start_date, 1)
  );
}

/** Concrete calendar occurrences; the template's start year is the first active year. */
export function seasonOccurrences(
  season: RepeatingSeasonRange,
  fromYear: number,
  toYear: number,
): Array<{ start_date: string; end_date: string }> {
  if (!validSeasonRange(season)) return [];
  if (season.repeat_type === "weekly") {
    const start = season.start_date > `${fromYear}-01-01` ? season.start_date : `${fromYear}-01-01`;
    const end = season.end_date < `${toYear}-12-31` ? season.end_date : `${toYear}-12-31`;
    const result: Array<{ start_date: string; end_date: string }> = [];
    for (const date = new Date(`${start}T00:00:00Z`); date.toISOString().slice(0, 10) <= end; date.setUTCDate(date.getUTCDate() + 1)) {
      if (season.weekly_days!.includes(date.getUTCDay())) {
        const value = date.toISOString().slice(0, 10);
        result.push({ start_date: value, end_date: value });
      }
    }
    return result;
  }
  if (season.repeat_type !== "yearly")
    return [{ start_date: season.start_date, end_date: season.end_date }];
  const anchor = Number(season.start_date.slice(0, 4));
  const result: Array<{ start_date: string; end_date: string }> = [];
  for (let year = Math.max(anchor, fromYear - 1); year <= toYear; year++) {
    const start_date = shiftYear(season.start_date, year - anchor);
    const end_date = shiftYear(season.end_date, year - anchor);
    if (start_date <= `${toYear}-12-31` && end_date >= `${fromYear}-01-01`)
      result.push({ start_date, end_date });
  }
  return result;
}

export function seasonContainsDate(
  season: RepeatingSeasonRange,
  date: string,
): boolean {
  if (!parts(date) || !validSeasonRange(season)) return false;
  if (season.repeat_type === "weekly")
    return season.start_date <= date && date <= season.end_date &&
      season.weekly_days!.includes(new Date(`${date}T00:00:00Z`).getUTCDay());
  if (season.repeat_type !== "yearly")
    return season.start_date <= date && date <= season.end_date;
  const year = Number(date.slice(0, 4));
  return seasonOccurrences(season, year, year).some(
    (range) => range.start_date <= date && date <= range.end_date,
  );
}

/** Annual patterns need one leap and one common year; eight years covers century gaps. */
export function seasonRangesOverlap(
  a: RepeatingSeasonRange,
  b: RepeatingSeasonRange,
): boolean {
  if (!validSeasonRange(a) || !validSeasonRange(b)) return false;
  // Date-specific rules override weekly defaults; only peers conflict.
  if ((a.repeat_type === "weekly") !== (b.repeat_type === "weekly")) return false;
  if (a.repeat_type === "weekly" && b.repeat_type === "weekly") {
    const start = a.start_date > b.start_date ? a.start_date : b.start_date;
    const end = a.end_date < b.end_date ? a.end_date : b.end_date;
    const date = new Date(`${start}T00:00:00Z`);
    // A shared weekday must occur inside the intersection, not just in either rule.
    for (let i = 0; i < 7 && date.toISOString().slice(0, 10) <= end; i++, date.setUTCDate(date.getUTCDate() + 1)) {
      const day = date.getUTCDay();
      if (a.weekly_days!.includes(day) && b.weekly_days!.includes(day)) return true;
    }
    return false;
  }
  const fixed =
    a.repeat_type !== "yearly" ? a : b.repeat_type !== "yearly" ? b : null;
  const from = fixed
    ? Number(fixed.start_date.slice(0, 4))
    : Math.max(
        Number(a.start_date.slice(0, 4)),
        Number(b.start_date.slice(0, 4)),
      );
  const to = fixed ? Number(fixed.end_date.slice(0, 4)) : from + 8;
  const ar = seasonOccurrences(a, from, to);
  const br = seasonOccurrences(b, from, to);
  return ar.some((x) =>
    br.some((y) => x.start_date <= y.end_date && x.end_date >= y.start_date),
  );
}

/** Date-specific/annual exceptions win over weekly defaults, regardless of row order. */
export function matchingSeasons<T extends RepeatingSeasonRange>(seasons: readonly T[], date: string): T[] {
  const matches = seasons.filter((s) => seasonContainsDate(s, date));
  return [...matches.filter((s) => s.repeat_type !== "weekly"), ...matches.filter((s) => s.repeat_type === "weekly")];
}

export function matchingSeason<T extends RepeatingSeasonRange>(seasons: readonly T[], date: string): T | null {
  return matchingSeasons(seasons, date)[0] ?? null;
}
